import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// INV-UX-001: read-only "what is reserving this stock?" drill-down. Reads the same reserved_qty the Inventory list shows;
// sources are the open pickup/delivery lines. No actions here — fulfilment stays in the Pickups workflow.
export function ReservedStockDialog({ product, onClose }) {
  const nav = useNavigate(); const [data, setData] = useState(null);
  useEffect(() => {
    if (!product) { setData(null); return; }
    api.get(`/products/${product.id}/reservations`).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e.response?.data?.detail)); onClose(); });
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps
  const when = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reserved-stock-dialog">
        <DialogHeader>
          <DialogTitle>Reserved stock · {product?.name}</DialogTitle>
          <DialogDescription>{product ? `${product.sku || "-"} · reserved ${product.reserved_qty} · available ${product.quantity} · on hand ${product.on_hand_qty}` : ""}</DialogDescription>
        </DialogHeader>
        {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
        {data && (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">Read-only. Collect, release or close these orders from the Pickups workflow.</p>
            {data.sources.length === 0 && <p className="text-muted-foreground" data-testid="reserved-sources-empty">No open pickup or delivery order currently holds this product.</p>}
            {data.sources.map((s) => (
              <div key={`${s.invoice_id}-${s.method}`} className="rounded-lg border border-border p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`reserved-source-${s.invoice_id}`}>
                <div className="space-y-0.5">
                  <div className="font-medium mono">{s.pickup_reference} <span className="text-muted-foreground">·</span> {s.invoice_number}</div>
                  <div className="text-xs text-muted-foreground">{s.method_label} · {s.customer_name || "Walk-in"} · reserved {when(s.reserved_at)}</div>
                  <Badge variant="outline" className="text-amber-700 border-amber-500/40" data-testid={`reserved-source-status-${s.invoice_id}`}>{s.status_label}</Badge>
                </div>
                <div className="text-right space-y-1">
                  <div className="mono font-bold" data-testid={`reserved-source-qty-${s.invoice_id}`}>{s.reserved_qty}</div>
                  <Button size="sm" variant="outline" onClick={() => { onClose(); nav(`/pickups?tab=${s.fulfillment_status?.startsWith("partially") ? "partial" : s.method === "delivery" ? "awaiting_delivery" : "awaiting_pickup"}&q=${encodeURIComponent(s.pickup_reference || s.invoice_number)}`); }} data-testid={`reserved-source-view-${s.invoice_id}`}>View Transaction</Button>
                </div>
              </div>
            ))}
            <div className="flex justify-between text-xs border-t border-border pt-2">
              <span className="text-muted-foreground">Sources total {data.sources_total} · Inventory reserved {data.reserved_qty}</span>
              <span className={data.reconciled ? "text-emerald-600" : "text-rose-600"} data-testid="reserved-reconciled">{data.reconciled ? "Reconciled" : "Does not reconcile — report to support"}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
